import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';

import {
  formatNumberForDisplay,
  formatWithThousandSeparator,
  parseFormattedNumber,
} from 'helpers/formatted-number.helpers';

interface UseFormattedNumberInputParams {
  value?: number | string;
  onChange: (value: number | string) => void;
  withSuffix: boolean;
  hasError: boolean;
}

export interface UseFormattedNumberInputResult {
  displayValue: string;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
}

const stripSeparators = (value: string): string => value.replace(/,/g, '');

export const useFormattedNumberInput = ({
  value,
  onChange,
  withSuffix,
  hasError,
}: UseFormattedNumberInputParams): UseFormattedNumberInputResult => {
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (value) {
      setDisplayValue(formatNumberForDisplay(value, withSuffix));
    } else if (!Number.isNaN(Number(value))) {
      setDisplayValue('');
    }
  }, [value, withSuffix]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    setDisplayValue(formatWithThousandSeparator(stripSeparators(nextValue)));

    if (hasError) onChange(nextValue);
  };

  const handleBlur = () => {
    const parsedValue = parseFormattedNumber(stripSeparators(displayValue));

    onChange(parsedValue);

    if (!Number.isNaN(parsedValue)) {
      setDisplayValue(formatNumberForDisplay(Number(parsedValue), withSuffix));
    }
  };

  return { displayValue, handleChange, handleBlur };
};
